import React from "react";
import { capitalizeName } from "../lib/utils/funcs";
import {
  patientNameMasking,
  petHospitalQueueImageClinics,
} from "../lib/utils/constants";
import SlotTimings from "./SlotTimings";
import { FaBell } from "react-icons/fa";
import { useTVConfiguration } from "../lib/contexts/TVConfigurationContext";
import RoomTag from "./RoomTag";
import { MdMeetingRoom } from "react-icons/md";
import { useFeatureFlags } from "../lib/contexts/FeatureFlagContext";
import { maskPatientName } from "../lib/utils/maskPatientName";

const Patient = ({
  pos,
  name,
  prn,
  notStarted,
  onGoing,
  empty,
  text,
  type,
  patientKey,
  carousel,
  status,
  isToggled = false,
  hospitalID,
  slotStartTime,
  slotEndTime,
  emergency,
  allotedRoom,
  showRoomName,
  onHold,
  transferStatus,
  recent_transfer_id,
  isTransferred,
}: {
  pos?: any;
  name?: string;
  prn?: string;
  notStarted?: boolean;
  onGoing?: boolean;
  empty?: boolean;
  text?: string;
  queue_type?: string;
  type?: string;
  patientKey?: number;
  carousel?: boolean;
  status?: number;
  isToggled?: boolean;
  hospitalID?: string | undefined;
  slotStartTime?: string | undefined;
  slotEndTime?: string | undefined;
  emergency?: boolean;
  allotedRoom?: string | undefined;
  showRoomName?: boolean;
  onHold?: boolean;
  transferStatus?: number | undefined;
  recent_transfer_id?: string | null | undefined;
  isTransferred?: boolean;
}) => {
  const { tvConfig } = useTVConfiguration();
  const { mask_patient_name_tv, display_prn_tv } = useFeatureFlags();

  if (carousel === undefined) {
    carousel = true;
  }

  // ✅ Masking logic
  const maskName = (fullName: string) => {
    if (!fullName) return "";

    const parts = fullName.trim().split(" ").filter(Boolean);

    const maskWord = (word: string) => {
      const len = word.length;
      if (len <= 2) return word;
      if (len === 3) return word[0] + "*" + word[2];
      if (len === 4) return word[0] + "**" + word[3];
      const firstTwo = word.slice(0, 2);
      const lastTwo = word.slice(-2);
      const middleStars = "*".repeat(len - 4);
      return firstTwo + middleStars + lastTwo;
    };

    if (parts.length === 1) return maskWord(parts[0]);

    const first = parts[0];
    const last = parts[parts.length - 1];

    const maskedFirst =
      first.length <= 2
        ? first
        : first.slice(0, 2) + "*".repeat(first.length - 2);

    const middleMasked = parts
      .slice(1, -1)
      .map((p) => "*".repeat(p.length))
      .join(" ");

    const maskedLast =
      last.length <= 2 ? last : "*".repeat(last.length - 2) + last.slice(-2);

    return [maskedFirst, middleMasked, maskedLast].filter(Boolean).join(" ");
  };

  // ✅ Conditionally mask the name based on hospitalID (normalized comparison)
  const shouldMask =
    hospitalID &&
    patientNameMasking.some(
      (id) => id.trim().toLowerCase() === hospitalID.trim().toLowerCase()
    );

  // display_prn_tv wins when the booking has a PRN — a PRN is not a name, so it
  // is shown as-is. With no PRN (the common case) the existing rules apply.
  const prnValue = display_prn_tv ? prn?.trim() : "";

  const displayName = prnValue
    ? prnValue
    : mask_patient_name_tv
    ? maskPatientName(name)
    : shouldMask
    ? maskName(capitalizeName(name || ""))
    : capitalizeName(name || "");

  // ✅ Common color logic for status/transferStatus
  const getStatusStyles = () => {
    if (onHold)
      return { bg: "bg-red-600", text: "text-white", label: "On-Hold" };
    if (transferStatus === 6)
      return { bg: "bg-[#7C3AED]", text: "text-white", label: "In History" }; // violet — queued
    if (transferStatus === 7)
      return { bg: "bg-[#10B018]", text: "text-white", label: "In History" }; // green — ongoing
    if (transferStatus === 8)
      return { bg: "bg-[#EA580C]", text: "text-white", label: "In Scan" }; // orange — queued
    if (transferStatus === 9)
      return { bg: "bg-[#10B018]", text: "text-white", label: "In Scan" }; // green — ongoing
    if (status === 2 || transferStatus === 2)
      return { bg: "bg-[#10B018]", text: "text-white", label: "Ongoing" }; // green ongoing

    // default for booking/walk-in/followup
    if (type === "walk-in")
      return { bg: "bg-[#0F3CBE]", text: "text-white", label: "Walk-in" };
    if (type === "followup")
      return { bg: "bg-[#0F3CBE]", text: "text-white", label: "Follow-up" };
    return { bg: "bg-[#0F3CBE]", text: "text-white", label: "Booking" };
  };

  const statusStyles = getStatusStyles();

  // ✅ Not Started
  if (notStarted) {
    return (
      <div className="bg-darkBlue p-5 my-5 mx-36 rounded-xl">
        <div className="rounded-[1rem]">
          <p className="text-white text-xl">Queue has not started</p>
        </div>
      </div>
    );
  }

  // ✅ Ongoing patient
  if (onGoing) {
    return (
      <div
        className={`${
          pos
            ? "relative flex flex-row bg-[#EBEBEB] mt-3 h-20 p-2 justify-center border-[3px] border-[#10B018] items-center rounded-full"
            : "relative flex flex-row bg-[#EBEBEB] mt-3 h-[10vh] p-2 justify-center border-[3px] border-[#10B018] items-center rounded-full"
        } ${isToggled ? "w-auto" : "w-full"}`}
      >

        {/* eslint-disable react/style-prop-object */}
        {!pos && !isTransferred && (!tvConfig || tvConfig.slot !== false) && (
          <SlotTimings
            start={slotStartTime}
            end={slotEndTime}
            isToggled={isToggled}
            style="!bg-black text-white"
          />
        )}
        {/* eslint-enable react/style-prop-object */}

        {pos && !isTransferred && (!tvConfig || tvConfig.token !== false) && tvConfig?.patient_name !== false && (
        <p className="flex text-md font-semibold bg-[#030303] text-white py-2 px-5 rounded-full justify-center items-center">
          Token {pos}
        </p>
      )}

      {isTransferred && (
        <span className="ml-2 px-3 py-1.5 text-sm font-bold bg-[#0F3CBE] text-white rounded-full">T</span>
      )}

      {/* Render NAME text if patient_name is VISIBLE */}
      {tvConfig?.patient_name !== false ? (
        <p className="text-2xl flex-[2.8] ml-4 font-medium uppercase">
          {displayName}
        </p>
      ) : // ELSE (patient_name is HIDDEN): Render TOKEN text if 'pos' exists
      pos && !isTransferred && (!tvConfig || tvConfig.token !== false) ? (
        <p className="text-2xl flex-[2.8] text-center font-medium uppercase">
          Token {pos}
        </p>
      ) : (
        // Fallback: Render empty div if BOTH name and token are hidden (shouldn't happen with admin rules)
        <div className="flex-[2.8] ml-4"></div>
      )}

        {showRoomName && allotedRoom ? (
          <RoomTag
            text={allotedRoom}
            showText={true}
            className={isToggled ? "flex-[0.8]" : "xl:flex-[0.4] md:flex-[0.8]"}
            icon={MdMeetingRoom}
          />
        ) : (
          (onHold ||
            status === 2 ||
            transferStatus === 2 ||
            (transferStatus !== undefined && [6, 7, 8, 9].includes(transferStatus)) ||
            !tvConfig ||
            tvConfig.booking_mode !== false) && (
            <p
              className={`text-md py-2 ${statusStyles.text} rounded-full ${
                statusStyles.bg
              } justify-center text-center mr-4 ${
                isToggled ? "flex-[0.8]" : "xl:flex-[0.4] md:flex-[0.8]"
              }`}
            >
              {statusStyles.label}
            </p>
          )
        )}

        {emergency && (
          <FaBell className="text-red-500 text-lg mr-2 w-6 h-6 hover:animate-bounce cursor-pointer transition-all duration-300 hover:scale-110" />
        )}
      </div>
    );
  }

  // ✅ Empty state
  if (empty) {
    const usePetQueueImage = petHospitalQueueImageClinics.includes(
      hospitalID || ""
    );

    return (
      <div className="flex flex-col items-center bg-white rounded-lg h-full mx-[20%] p-5 justify-center">
        <img
          src={
            usePetQueueImage
              ? "https://ik.imagekit.io/gn9by35ed/vetClinicQueueImage1-removebg-preview.png?updatedAt=1748851163134"
              : require("../assets/images/empty.png")
          }
          alt="Queue empty"
          className={carousel || !isToggled ? "!w-[100%]" : "!w-[100%]"}
        />
        <p className="text-center mb-5 font-medium">{text}</p>
      </div>
    );
  }

  // ✅ Normal waiting patient
  return (
    <div
      className={`flex flex-row bg-[#EBEBEB] mt-3 h-20 p-2 justify-center items-center rounded-full ${
        isToggled ? "w-auto" : "w-full"
      }`}
    >
      
      {!pos && !isTransferred && (!tvConfig || tvConfig.slot !== false) && (
        <SlotTimings
          start={slotStartTime}
          end={slotEndTime}
          isToggled={isToggled}
        />
      )}

      {pos && !isTransferred && (!tvConfig || tvConfig.token !== false) && tvConfig?.patient_name !== false && (
        <p className="flex text-md font-semibold bg-[#030303] text-white py-2 px-5 rounded-full justify-center items-center">
          Token {pos}
        </p>
      )}

      {isTransferred && (
        <span className="ml-2 px-3 py-1.5 text-sm font-bold bg-[#0F3CBE] text-white rounded-full">T</span>
      )}

      {/* Render NAME text if patient_name is VISIBLE */}
      {tvConfig?.patient_name !== false ? (
        <p className="text-2xl flex-[2.8] ml-4 font-medium uppercase truncate">
          {displayName}
        </p>
      ) : // ELSE (patient_name is HIDDEN): Render TOKEN text if 'pos' exists
      pos && !isTransferred && (!tvConfig || tvConfig.token !== false) ? (
        <p className="text-2xl flex-[2.8] text-center font-medium uppercase truncate">
          Token {pos}
        </p>
      ) : (
        // Fallback: Render empty div if BOTH name and token are hidden
        <div className="flex-[2.8] ml-4"></div>
      )}

      {(onHold ||
        status === 2 ||
        transferStatus === 2 ||
        (transferStatus !== undefined && [6, 7, 8, 9].includes(transferStatus)) ||
        !tvConfig ||
        tvConfig.booking_mode !== false) && (
        <p
          className={`text-md py-2 ${statusStyles.text} ${
            statusStyles.bg
          } rounded-full justify-center text-center mr-4 ${
            isToggled ? "flex-[0.8]" : "xl:flex-[0.4] md:flex-[0.8]"
          }`}
        >
          {statusStyles.label}
        </p>
      )}
    </div>
  );
};

export default Patient;
